import { useEffect, useState } from "react"
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
} from "@mui/material"
import InfiniteScroll from "react-infinite-scroll-component"
import {
  useGetCollectionByHandleMutation,
  useGetProductsByQueryMutation,
} from "src/features/checkout/storefrontApi"
import { useDispatch, useSelector } from "react-redux"
import { AddShoppingCart } from "@mui/icons-material"
import { currencyFormat } from "src/app/utils"
import useAddToCart from "src/app/hooks/useAddToCart"
import { setActiveProduct } from "src/features/checkout/checkoutSlice"
import { DEFAULT_PRODUCT_IMAGE } from "src/app/constants"
import { LoadingButton } from "@mui/lab"

function AddToCartButton({ product }) {
  const [addToCart, { isLoading }] = useAddToCart()

  return (
    <LoadingButton
      loading={isLoading}
      color="primary"
      startIcon={<AddShoppingCart />}
      onClick={() => addToCart(product)}
    >
      Add to Cart
    </LoadingButton>
  )
}

export default function AllProductsInfiniteScroll() {
  const dispatch = useDispatch()
  const theme = useTheme()
  const query = useSelector((state) => state.checkout.allProductsSearchQuery)
  const [products, setProducts] = useState([])
  const [hasNextPage, setHasNextPage] = useState(false)

  const [addToCart] = useAddToCart()

  const [
    getCollectionByHandle,
    {
      data: getCollectionByHandleData,
      isLoading: getCollectionByHandleLoading,
      isFetching: getCollectionByHandleFetching,
    },
  ] = useGetCollectionByHandleMutation()

  const [
    getProductsByQuery,
    {
      data: getProductsByQueryData,
      isLoading: getProductsByQueryLoading,
      isFetching: getProductsByQueryFetching,
    },
  ] = useGetProductsByQueryMutation()

  const styles = {
    cardGrid: {
      padding: theme.spacing(2) + "px !important",
    },
    card: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
    },
    cardMedia: {
      paddingTop: "56.25%", // 16:9
      backgroundSize: "contain",
    },
    cardActions: {
      justifyContent: "center",
    },
    cardContent: {
      flexGrow: 1,
    },
  }

  const getProducts = (withCursor = true) => {
    getCollectionByHandle({
      handle: "wine-shop",
      after: withCursor ? products[products.length - 1]?.cursor || null : null,
    })
  }

  const reset = () => {
    setHasNextPage(false)
    setProducts([])
  }

  const search = (withCursor = true) => {
    if (query.length) {
      getProductsByQuery({
        query: `${query.replace(" ", "+")} AND available_for_sale:true`,
        after: withCursor
          ? products[products.length - 1]?.cursor || null
          : null,
      })
    }
  }

  useEffect(() => {
    return () => reset() // Reset on unmount
  }, [])

  useEffect(() => {
    reset() // Reset products if search query has been changed.
    if (query.length) {
      setTimeout(() => search(false), 100) // Do a search if there's a search query.
    } else {
      setTimeout(() => getProducts(false), 100) // Get wine collection products.
    }
  }, [query])

  useEffect(() => {
    if (!getCollectionByHandleData) return

    if (getCollectionByHandleData.collectionByHandle?.products?.edges?.length) {
      setProducts(
        products.concat(
          getCollectionByHandleData.collectionByHandle?.products?.edges,
        ),
      )
    }
    setHasNextPage(
      getCollectionByHandleData.collectionByHandle?.products?.pageInfo
        ?.hasNextPage || false,
    )
  }, [getCollectionByHandleData])

  useEffect(() => {
    if (!getProductsByQueryData?.products) return

    if (getProductsByQueryData.products.edges.length) {
      setProducts(products.concat(getProductsByQueryData.products.edges))
    }
    setHasNextPage(
      getProductsByQueryData.products.pageInfo?.hasNextPage || false,
    )
  }, [getProductsByQueryData])

  return (
    <>
      <InfiniteScroll
        dataLength={products.length}
        next={query.trim().length ? search : getProducts}
        hasMore={hasNextPage}
        loader={
          <Box
            m={2}
            mt="10%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress />
          </Box>
        }
        scrollableTarget="all-products-content"
      >
        <Grid container spacing={3}>
          {products.map((data) => {
            const product = data.node
            return (
              <Grid
                key={product.id}
                sx={styles.cardGrid}
                item
                xs={12}
                sm={4}
                md={3}
                lg={2}
              >
                <Card sx={styles.card}>
                  <CardActionArea
                    onClick={() => {
                      dispatch(setActiveProduct(product))
                    }}
                  >
                    <CardMedia
                      sx={styles.cardMedia}
                      image={
                        product.images.edges[0]?.node.url ||
                        DEFAULT_PRODUCT_IMAGE
                      }
                      title={product.images.edges[0]?.node.altText || ""}
                    />
                    <CardContent sx={styles.cardContent}>
                      <Typography variant="body2" noWrap>
                        {product.title}
                      </Typography>
                      <Typography variant="body1">
                        {currencyFormat(
                          product.variants.edges[0].node.price.amount,
                        )}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                  <CardActions sx={styles.cardActions}>
                    <AddToCartButton product={product} />
                  </CardActions>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </InfiniteScroll>

      <Box textAlign="center" mt="10%">
        {(getCollectionByHandleLoading ||
          getCollectionByHandleFetching ||
          getProductsByQueryLoading ||
          getProductsByQueryFetching) &&
        !products.length ? (
          <CircularProgress />
        ) : query.length && !products.length ? (
          <Typography>No products found for: {query}</Typography>
        ) : (
          ""
        )}
      </Box>
    </>
  )
}
